import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useReconciliation = () => {
  const navigate = useNavigate();
  const [reconciliations, setReconciliations] = useState([]);
  const [reconciliationDetails, setReconciliationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  /**
   * Fetch all daily reconciliations with optional filters
   * @param {Object} params - Query parameters for filtering
   * @param {string} params.status - Reconciliation status (In Progress, Completed, Requires Review)
   * @param {string} params.reconciliation_date - Specific date filter
   * @param {string} params.date_from - Start date filter
   * @param {string} params.date_to - End date filter
   */
  const fetchReconciliations = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {
          page: params.page || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
        };

        // Add filters if provided
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.reconciliations}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          setReconciliations(Array.isArray(data) ? data : data.results || []);
          if (data.count) {
            setPagination((prev) => ({ ...prev, total: data.count }));
          }
        } else {
          Utilities.showError(response, "Failed to fetch reconciliations");
        }
      } catch (error) {
        message.error("An error occurred while fetching reconciliations");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate, pagination.current, pagination.pageSize],
  );

  /**
   * Fetch reconciliation details by ID
   * @param {number} id - Reconciliation ID
   */
  const fetchReconciliationDetails = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.reconciliationDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setReconciliationDetails(response.data);
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to fetch reconciliation details",
          );
          return null;
        }
      } catch (error) {
        message.error(
          "An error occurred while fetching reconciliation details",
        );
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Create a new daily reconciliation
   * @param {Object} data - Reconciliation data
   * @param {string} data.reconciliationDate - Date for reconciliation (YYYY-MM-DD)
   * @param {string} data.notes - Optional notes
   * @param {File} data.bankStatementFile - Optional bank statement file
   */
  const createReconciliation = useCallback(
    async (data) => {
      setLoading(true);
      try {
        let requestData;

        // Handle file upload with FormData
        if (data.bankStatementFile) {
          const formData = new FormData();
          formData.append("reconciliationDate", data.reconciliationDate);
          if (data.notes) formData.append("notes", data.notes);
          formData.append("bankStatementFile", data.bankStatementFile);
          requestData = formData;
        } else {
          requestData = {
            reconciliationDate: data.reconciliationDate,
            notes: data.notes || "",
          };
        }

        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.reconciliations,
          requestData,
          navigate,
        );

        if (response.ok) {
          message.success("Reconciliation created successfully");
          fetchReconciliations({ page: 1 });
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create reconciliation");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while creating reconciliation");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchReconciliations],
  );

  /**
   * Update reconciliation status and/or file
   * @param {number} id - Reconciliation ID
   * @param {Object} data - Update data
   * @param {string} data.status - New status
   * @param {string} data.notes - Updated notes
   * @param {File} data.bankStatementFile - Bank statement file (optional)
   * @param {boolean} data.removeBankStatement - Set to true to remove the file
   */
  const updateReconciliation = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.reconciliationDetail.replace(
          ":id",
          id,
        );
        let requestData;

        // Handle file upload with FormData
        if (data.bankStatementFile || data.removeBankStatement) {
          const formData = new FormData();
          if (data.status) formData.append("status", data.status);
          if (data.notes !== undefined)
            formData.append("notes", data.notes || "");
          if (data.bankStatementFile) {
            formData.append("bankStatementFile", data.bankStatementFile);
          }
          if (data.removeBankStatement) {
            formData.append("bankStatementFile", ""); // Empty string to clear
          }
          requestData = formData;
        } else {
          requestData = {};
          if (data.status) requestData.status = data.status;
          if (data.notes !== undefined) requestData.notes = data.notes;
        }

        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          requestData,
          navigate,
        );

        if (response.ok) {
          message.success("Reconciliation updated successfully");
          fetchReconciliations();
          // Also refresh details if we have them loaded
          if (reconciliationDetails && reconciliationDetails.id === id) {
            fetchReconciliationDetails(id);
          }
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update reconciliation");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while updating reconciliation");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      fetchReconciliations,
      fetchReconciliationDetails,
      reconciliationDetails,
    ],
  );

  /**
   * Complete a reconciliation (mark as Completed)
   * @param {number} id - Reconciliation ID
   * @param {string} notes - Completion notes
   */
  const completeReconciliation = useCallback(
    async (id, notes = "") => {
      return updateReconciliation(id, { status: "Completed", notes });
    },
    [updateReconciliation],
  );

  /**
   * Mark reconciliation as requiring review
   * @param {number} id - Reconciliation ID
   * @param {string} notes - Review notes
   */
  const markForReview = useCallback(
    async (id, notes = "") => {
      return updateReconciliation(id, { status: "Requires Review", notes });
    },
    [updateReconciliation],
  );

  /**
   * Delete a reconciliation record
   * @param {number} id - Reconciliation ID
   */
  const deleteReconciliation = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.finance.reconciliationDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Reconciliation deleted successfully");
          fetchReconciliations();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete reconciliation");
          return false;
        }
      } catch (error) {
        message.error("An error occurred while deleting reconciliation");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchReconciliations],
  );

  /**
   * Calculate reconciliation statistics from the list
   */
  const getStatistics = useCallback(() => {
    const stats = {
      total: reconciliations.length,
      completed: reconciliations.filter((r) => r.status === "Completed").length,
      inProgress: reconciliations.filter((r) => r.status === "In Progress")
        .length,
      requiresReview: reconciliations.filter(
        (r) => r.status === "Requires Review",
      ).length,
      totalPayments: reconciliations.reduce(
        (sum, r) => sum + parseFloat(r.totalPayments || 0),
        0,
      ),
      totalValidPayments: reconciliations.reduce(
        (sum, r) => sum + parseFloat(r.totalValidPayments || 0),
        0,
      ),
      totalSuspensePayments: reconciliations.reduce(
        (sum, r) => sum + parseFloat(r.totalSuspensePayments || 0),
        0,
      ),
      totalPaymentCount: reconciliations.reduce(
        (sum, r) => sum + (r.paymentCount || 0),
        0,
      ),
      totalSuspenseCount: reconciliations.reduce(
        (sum, r) => sum + (r.suspenseCount || 0),
        0,
      ),
      get totalPendingPayments() {
        return Math.max(
          0,
          this.totalPayments -
            this.totalValidPayments -
            this.totalSuspensePayments,
        );
      },
    };
    return stats;
  }, [reconciliations]);

  return {
    reconciliations,
    reconciliationDetails,
    loading,
    pagination,
    setPagination,
    fetchReconciliations,
    fetchReconciliationDetails,
    createReconciliation,
    updateReconciliation,
    completeReconciliation,
    markForReview,
    deleteReconciliation,
    getStatistics,
  };
};

export default useReconciliation;
